import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { useLocation as useAppLocation } from '../../contexts/LocationContext';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import './CalculatorPage.css';

const CoolClimateCalculator = lazy(() =>
  import('./CoolClimateCalculator').then((m) => ({ default: m.CoolClimateCalculator })),
);

const IFRAME_LOAD_TIMEOUT_MS = 12000;
const CALCULATOR_HEIGHT_MESSAGE = 'calculator-height';
const IFRAME_MIN_HEIGHT_PX = 640;
/** Summary + “show all” opportunities can exceed 10kpx; keep a high safety ceiling only. */
const IFRAME_ABSOLUTE_MAX_HEIGHT_PX = 20000;
/** Extra height so the iframe doesn't clip the bottom (e.g. Next button, chart labels) */
const IFRAME_HEIGHT_BUFFER_PX = 32;

function clampEmbedHeight(contentHeight: number): number {
  const h = Math.max(IFRAME_MIN_HEIGHT_PX, contentHeight + IFRAME_HEIGHT_BUFFER_PX);
  return Math.min(IFRAME_ABSOLUTE_MAX_HEIGHT_PX, h);
}
/** Local dev: Next.js calculator on :3001 (ClimatePlans uses :3000). */
const LOCAL_CALCULATOR_DEV_URL = 'http://localhost:3001/calculator';

/**
 * Calculator tab page: embeds the CoolClimate calculator (new Next.js app) in an iframe.
 * Only visible to developers and super admins (not local government admins). URL is configurable via REACT_APP_CALCULATOR_URL.
 * When a ClimatePlans location is selected, passes ?location= and ?geofips= so the calculator pre-fills "Davis, California" and loads that place's defaults (geofips avoids leading-zero and name ambiguity).
 */
export const CalculatorPage: React.FC = () => {
  const { locationSlug } = useParams<{ locationSlug: string }>();
  const { selectedLocation, selectedGeofips, selectLocation, getGeofipsForLocation, locationData } =
    useAppLocation();
  // Re-run when slug or location catalog loads (peek reads service catalog; tie memo to catalog size).
  const locationCatalog = locationData?.availableLocations;
  const locationFromSlug = useMemo(() => {
    if (!locationSlug) return '';
    // Invalidate when catalog hydrates (length changes) without relying on eslint-disable.
    void locationCatalog?.length;
    return supabaseLocationService.peekLocationBySlug(locationSlug)?.name ?? '';
  }, [locationSlug, locationCatalog]);
  const effectiveLocation = locationFromSlug || selectedLocation;
  const geofipsFromContext =
    locationFromSlug && selectedLocation && locationFromSlug !== selectedLocation
      ? null
      : selectedGeofips;
  const [resolvedGeofips, setResolvedGeofips] = useState<number | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);
  // Resolve geofips when we have a location but context hasn't set selectedGeofips yet (e.g. direct nav to calculator tab)
  const geofipsForUrl = geofipsFromContext ?? resolvedGeofips;
  const [geofipsTimedOut, setGeofipsTimedOut] = useState(false);

  // Keep context synchronized with slugged URLs like /pacific-grove/coolclimate-calculator.
  useEffect(() => {
    if (!locationFromSlug) return;
    if (selectedLocation === locationFromSlug) return;
    void selectLocation(locationFromSlug);
  }, [locationFromSlug, selectedLocation, selectLocation]);

  useEffect(() => {
    if (!effectiveLocation?.trim() || geofipsFromContext != null) {
      setResolvedGeofips(null);
      setGeofipsTimedOut(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      getGeofipsForLocation(effectiveLocation).then((g) => {
        if (!cancelled && g != null) setResolvedGeofips(g);
      });
    }, 0);
    const timeout = setTimeout(() => {
      if (!cancelled && (geofipsFromContext ?? resolvedGeofips) == null) setGeofipsTimedOut(true);
    }, 2500);
    return () => {
      cancelled = true;
      clearTimeout(t);
      clearTimeout(timeout);
    };
    // resolvedGeofips intentionally omitted: effect runs only when location/selection changes, not when resolution completes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLocation, geofipsFromContext, getGeofipsForLocation]);

  const isLocalhostHost = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '::1'
    );
  }, []);

  // Env wins; else same-origin /calculator (production proxy); else local dev calculator on :3001.
  // Never default to localhost:3000 — that is the ClimatePlans app.
  const baseUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const fallbackSameOriginCalculator =
      origin ? `${origin}/calculator` : '';
    const fallbackLocal = isLocalhostHost ? LOCAL_CALCULATOR_DEV_URL : '';

    let fromEnv = process.env.REACT_APP_CALCULATOR_URL?.trim() || '';
    if (fromEnv) {
      try {
        const abs =
          fromEnv.startsWith('/') && origin
            ? new URL(fromEnv, origin).href
            : new URL(fromEnv).href;
        const parsed = new URL(abs);
        const isLocalCalculatorHost =
          parsed.hostname === 'localhost' ||
          parsed.hostname === '127.0.0.1' ||
          parsed.hostname === '::1';
        if (isLocalCalculatorHost && !isLocalhostHost) {
          fromEnv = '';
        } else {
          fromEnv = abs;
        }
      } catch {
        fromEnv = '';
      }
    }

    let candidate = fromEnv || fallbackSameOriginCalculator || fallbackLocal;
    if (!candidate) return '';

    try {
      const parsed = new URL(candidate);
      // Avoid embedding ClimatePlans itself (e.g. http://localhost:3000 with no /calculator path).
      if (origin && parsed.origin === origin) {
        const path = parsed.pathname.replace(/\/$/, '') || '/';
        if (path === '/') {
          return isLocalhostHost ? LOCAL_CALCULATOR_DEV_URL : fallbackSameOriginCalculator;
        }
      }
      return parsed.href;
    } catch {
      return fallbackSameOriginCalculator || fallbackLocal || '';
    }
  }, [isLocalhostHost]);

  /** Embed the Next.js calculator in an iframe whenever we have a valid calculator URL. */
  const useIframeEmbed = useMemo(() => {
    if (!baseUrl) return false;
    try {
      const parsed = new URL(baseUrl);
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      if (origin && parsed.origin === origin) {
        return parsed.pathname.startsWith('/calculator');
      }
      return true;
    } catch {
      return false;
    }
  }, [baseUrl]);

  // Pass location + geofips when we have geofips; after 2.5s without geofips still load with ?location= so calculator can try name search ("Berkeley, California").
  const calculatorSrc = useMemo(() => {
    if (!baseUrl) return '';
    const params = new URLSearchParams();
    params.set('takeAction', '1');
    if (!effectiveLocation?.trim()) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}${params.toString()}`;
    }
    const geofips = geofipsForUrl != null && Number.isFinite(geofipsForUrl) ? geofipsForUrl : null;
    // Calculator expects "City, California" (not just "Berkeley") for valid lookups and defaults
    const isCalifornia = geofips != null && Math.floor(geofips / 100000) === 6;
    const locationParam =
      isCalifornia && !/,\s*(California|CA)\s*$/i.test(effectiveLocation.trim())
        ? effectiveLocation.trim() + ', California'
        : effectiveLocation.trim();
    params.set('location', locationParam);
    if (geofips != null) {
      const padded = String(geofips).padStart(7, '0').slice(-7);
      params.set('geofips', padded);
    }
    if (geofips == null && !geofipsTimedOut) return ''; // wait for geofips (or timeout) before loading
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${params.toString()}`;
  }, [baseUrl, effectiveLocation, geofipsForUrl, geofipsTimedOut]);

  // When iframe src changes, reset load state and height so we show spinner again
  useEffect(() => {
    if (!calculatorSrc) return;
    setIframeLoaded(false);
    setLoadTimedOut(false);
    setIframeHeight(null);
  }, [calculatorSrc]);

  // Listen for height from calculator so we can size the iframe and avoid clipping the chart.
  // Local/staging embeds are often cross-origin (e.g. :3000 host → :3001 calculator).
  useEffect(() => {
    let allowedOrigin: string | null = null;
    try {
      if (baseUrl) allowedOrigin = new URL(baseUrl).origin;
    } catch {
      allowedOrigin = null;
    }

    const handler = (event: MessageEvent) => {
      if (event.data?.type !== CALCULATOR_HEIGHT_MESSAGE || typeof event.data.height !== 'number') {
        return;
      }
      const sameOrigin =
        typeof window !== 'undefined' && event.origin === window.location.origin;
      const fromCalculator = allowedOrigin != null && event.origin === allowedOrigin;
      if (!sameOrigin && !fromCalculator) {
        return;
      }
      const contentHeight = event.data.height + IFRAME_HEIGHT_BUFFER_PX;
      setIframeHeight(clampEmbedHeight(contentHeight));
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [baseUrl]);

  // If the iframe never fires onLoad (e.g. calculator not running), stop spinner after timeout
  useEffect(() => {
    if (!calculatorSrc || iframeLoaded) return;
    const t = setTimeout(() => {
      setIframeLoaded(true);
      setLoadTimedOut(true);
    }, IFRAME_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [calculatorSrc, iframeLoaded]);

  return (
    <div
      className={`calculator-page${iframeHeight != null ? ' calculator-page--sized' : ''}${
        useIframeEmbed ? ' calculator-page--iframe-embed' : ''
      }`}
    >
      <div className="calculator-page-content">
        {!useIframeEmbed ? (
          <Suspense
            fallback={
              <div className="calculator-page-iframe-wrap" style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="calculator-page-loading">
                  <div className="loading-spinner" />
                  <p>Loading calculator…</p>
                </div>
              </div>
            }
          >
            <CoolClimateCalculator embedded />
          </Suspense>
        ) : !baseUrl ? (
          <div className="calculator-page-placeholder">
            <p>Calculator URL could not be determined.</p>
            <p>
              Set a valid <code>REACT_APP_CALCULATOR_URL</code> in <code>.env</code> and rebuild.
              On production, the app normally uses <code>/calculator</code> on this site (reverse proxy).
              For local dev, run the calculator on port 3001: <code>cd coolclimate-calculator &amp;&amp; npm run dev -- -p 3001</code>
              (do not run coolclimate-portal on 3001 — that is a different app). Set{' '}
              <code>REACT_APP_CALCULATOR_URL=http://localhost:3001/calculator</code> in <code>.env.local</code>.
            </p>
          </div>
        ) : !calculatorSrc && effectiveLocation?.trim() ? (
          <div className="calculator-page-iframe-wrap" style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="calculator-page-loading">
              <div className="loading-spinner" />
              <p>Resolving location for {effectiveLocation}…</p>
            </div>
          </div>
        ) : (
          <div
            className="calculator-page-iframe-wrap"
            style={{
              height: iframeHeight != null ? `${iframeHeight}px` : undefined,
              minHeight: IFRAME_MIN_HEIGHT_PX,
            }}
          >
            {!iframeLoaded && (
              <div className="calculator-page-loading">
                <div className="loading-spinner" />
                <p>Loading calculator…</p>
              </div>
            )}
            <iframe
              src={calculatorSrc}
              title="CoolClimate Calculator"
              className={`calculator-page-iframe ${iframeLoaded ? 'loaded' : ''}`}
              style={{
                height: iframeHeight != null ? `${iframeHeight}px` : IFRAME_MIN_HEIGHT_PX,
                minHeight: IFRAME_MIN_HEIGHT_PX,
              }}
              scrolling="no"
              allow="clipboard-write; web-share"
              onLoad={() => {
                setIframeLoaded(true);
                setLoadTimedOut(false);
              }}
            />
            {loadTimedOut && (
              <p className="calculator-page-timeout-hint">
                If the calculator is blank, start the Next.js calculator (not coolclimate-portal):{' '}
                <code>cd coolclimate-calculator &amp;&amp; npm run dev -- -p 3001</code>
                and set <code>REACT_APP_CALCULATOR_URL=http://localhost:3001/calculator</code> in ClimatePlans <code>.env.local</code>.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
